/* eslint-disable no-unused-vars */
import { yupResolver } from "@hookform/resolvers/yup";
import Loader from "common/Loader";
import { setToaster } from "common/toastStore";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import AdminServices from "services/admin-services";
import TokenService from "services/TokenService";
import { IAddPackage } from "types/customers.type";
import * as Yup from "yup";
import { handlingUrlParams } from "Utils/AppUtils";

interface AddUsersProps {
  onClose: () => void;
  customerId: number;
  onSubmitSuccess: () => void;
}

const AddPackage: FunctionComponent<AddUsersProps> = ({
  onClose,
  customerId,
  onSubmitSuccess,
}) => {
  const [customerData, setCustomerData] = useState<any>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const getCusomerObj = TokenService.getCustomerData();
  const [checked, setChecked] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [packageDocumentFile, setPackageDocumentFile] = useState<File | null>(
    null
  );
  const [inputValue, setInputValue] = useState<IAddPackage>({
    customerId: 0,
    name: "",
    description: "",
    isActive: true,
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({
      ...value,
      [name]: value,
    });
  };

  const validationSchema = Yup.object().shape({
    customerId: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? undefined : value
      )
      .typeError("Customer ID must be a number")
      .required("Customer ID is required"),
    name: Yup.string().required("Name is required"),
    description: Yup.string().required("Description is required"),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: "all",
  };
  const { register, handleSubmit, formState, setValue } = useForm<IAddPackage>(
    formOptions as object
  );
  const { errors } = formState;

  const onSubmit = (data: any) => {
    setLoading(true);

    const queryPayload = {
      customerId: customerId,
      name: data.name,
      description: data.description,
      isActive: checked,
    };

    const params = handlingUrlParams(queryPayload);
    const formData = new FormData();

    if (packageDocumentFile) {
      formData.append("PackageDocumentFile", packageDocumentFile);
    }

    AdminServices.addPackages(params, formData)
      .then((_response: any) => {
        setLoading(false);
        setToaster({
          severity: "success",
          summary: "Success ",
          detail: `Package Added successfully!`,
        });
        onClose();
        onSubmitSuccess();
      })
      .catch((e: any) => {
        setLoading(false);
        setToaster({
          severity: "error",
          summary: "Error Message",
          detail: `${e?.response?.data}`,
        });
      });
  };

  const handleCustomerChange = (selected: any) => {
    setSelectedCustomer(selected);
    setValue("customerId", selected?.value ?? 0, { shouldValidate: true });
  };

  const getCusomerData = () => {
    let customerDataArray: { label: string; value: number }[] = [];
    getCusomerObj?.forEach((element: { name: string; id: number }) => {
      customerDataArray.push({
        label: element.name,
        value: element.id,
      });
    });
    setCustomerData(customerDataArray);

    if (customerDataArray.length === 1) {
      const singleCustomer = customerDataArray[0];
      setSelectedCustomer(singleCustomer);
      setValue("customerId", singleCustomer.value, { shouldValidate: true });
    }
  };

  useEffect(() => {
    getCusomerData();
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="py-5">
        <div className="grid grid-cols-1 gap-8">
          <div>
            <div className="p-float-label">
              <Select
                options={customerData}
                placeholder="Select customer ID"
                value={selectedCustomer}
                onChange={handleCustomerChange}
                name="customerId"
              />
              {errors.customerId && (
                <div className="text-xs text-red-900 mt-1">
                  {errors.customerId.message}
                </div>
              )}
              {/* <InputText
                type="number"
                id="customerId"
                {...register("customerId")}
                name="customerId"
                onChange={handleChange}
                className="w-full text-sm"
              />
              <label htmlFor="customerId">Enter Customer ID</label> */}
            </div>
          </div>
          <div>
            <div className="p-float-label">
              <InputText
                id="name"
                {...register("name")}
                name="name"
                onChange={handleChange}
                className="w-full text-sm"
              />
              <label htmlFor="name">Enter Name</label>
            </div>
            <div className="text-xs text-red-900">{errors?.name?.message}</div>
          </div>
          <div>
            <div className="p-float-label">
              <InputText
                id="description"
                {...register("description")}
                name="description"
                onChange={handleChange}
                className="w-full text-sm"
              />
              <label htmlFor="description">Enter Description</label>
            </div>
            <div className="text-xs text-red-900">
              {errors?.description?.message}
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="packageDocumentFile" className="mb-1 block text-sm">
            Package Document File (Optional)
          </label>
          <FileUpload
            auto
            name="packageDocumentFile"
            accept=".csv, .xls, .xlsx, image/*, .pdf"
            maxFileSize={1000000}
            customUpload
            multiple={false}
            uploadHandler={(e) => {
              const file = e.files?.[0] || null;
              setPackageDocumentFile(file);
            }}
            onClear={() => {
              setPackageDocumentFile(null);
            }}
            chooseLabel="Select File"
            emptyTemplate={
              <p className="m-0">Drag and drop file to here to upload.</p>
            }
          />
          {packageDocumentFile && (
            <div className="mt-1 text-xs text-gray-600">
              Selected: {packageDocumentFile.name}
            </div>
          )}
        </div>

        <div className="flex flex-row items-center mt-6">
          <div className="field-checkbox">
            <Checkbox
              inputId="isSuperAdmin"
              checked={checked}
              onChange={(e) => {
                setChecked(e.checked ?? false);
              }}
            />
            <label htmlFor="isSuperAdmin" className="px-3">
              Active
            </label>
          </div>
        </div>

        <div className="mt-8 flex flex-row-reverse items-center">
          <Button
            label="Save"
            type="submit"
            icon="pi pi-check"
            className="p-button-sm p-button-success p-button-outlined"
          />
          <div className="mx-2"></div>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={onClose}
            className="p-button-sm p-button-danger p-button-outlined"
          />
        </div>
      </form>
      {loading ? <Loader /> : null}
    </>
  );
};

export default AddPackage;
